import { basename, dirname, join } from "path";
import {
  collectToolNames,
  extractTextContent,
  extractThinking,
  extractToolUseBlocks,
  isOnlyToolResultContent,
  isTeammateContent,
} from "../parser";
import { CLAUDE_CODE_PROVIDER } from "../providers/provider";
import { type NameSlots, resolveSessionName } from "../session-name";
import type { ContentTier, ConversationMeta, MessageSender, MessageSnapshot } from "../types";
import { applyClaudeNameEntry } from "./claude-name-slots";

// Serializable fold state for incremental metadata indexing. parseMeta()'s
// per-line loop is expressed as reduceLine(state, entry); finalizeMeta(state)
// produces the ConversationMeta. Because the state is plain JSON, it can be
// persisted in conversation_files.reducer_state and resumed when a file grows,
// so an append only needs to fold the newly-read lines.
export interface ReducerState {
  sessionId: string;
  nameSlots: NameSlots;
  latestTimestamp: string;
  cwd: string;
  teamName: string;
  model: string | null;
  messageCount: number;
  // Count of messages parseConversation() would produce — broader than
  // messageCount (also counts tool_use-only and thinking-only lines). Used as
  // the page total for bounded reads, since metadata messageCount differs.
  pageMessageCount: number;
  lastMessageSender: MessageSender;
  // Provider-created child ("sidechain") identity, lifted from the explicit
  // per-line JSONL fields. A sidechain line carries its PARENT's id in
  // `sessionId`, so `agentId` is the only thing that distinguishes siblings.
  agentId: string;
  isSidechain: boolean;
  isTeammate: boolean;
  firstUserSeen: boolean;
  firstMessage: MessageSnapshot | null;
  lastMessage: MessageSnapshot | null;
  lastPrompt: string;
  toolNames: string[];
  previewParts: string[];
  snippetParts: string[];
  previewLength: number;
  snippetLength: number;
  badJsonLines: number;
}

export function initialReducerState(): ReducerState {
  return {
    sessionId: "",
    nameSlots: {},
    latestTimestamp: "",
    cwd: "",
    teamName: "",
    model: null,
    messageCount: 0,
    lastMessageSender: "user",
    agentId: "",
    isSidechain: false,
    isTeammate: false,
    firstUserSeen: false,
    firstMessage: null,
    lastMessage: null,
    lastPrompt: "",
    pageMessageCount: 0,
    toolNames: [],
    previewParts: [],
    snippetParts: [],
    previewLength: 0,
    snippetLength: 0,
    badJsonLines: 0,
  };
}

// Fold one already-parsed JSONL entry into the state, mutating it in place.
// Mirrors parseMeta()'s loop body exactly so a full reduce equals a streamed
// parseMeta. `entry` is the JSON.parse() of one non-empty line.
export function reduceLine(
  state: ReducerState,
  entry: Record<string, unknown>,
  tier: ContentTier,
): void {
  if (entry.cwd && !state.cwd) state.cwd = entry.cwd as string;
  if (entry.sessionId && !state.sessionId) state.sessionId = entry.sessionId as string;
  state.nameSlots = applyClaudeNameEntry(state.nameSlots, entry);
  if (entry.teamName && !state.teamName) state.teamName = entry.teamName as string;
  if (entry.agentId && !state.agentId) state.agentId = entry.agentId as string;
  if (entry.isSidechain === true) state.isSidechain = true;
  if (entry.timestamp) {
    const ts = entry.timestamp as string;
    if (!state.latestTimestamp || ts > state.latestTimestamp) state.latestTimestamp = ts;
  }

  const type = entry.type as string;

  if (type === "last-prompt") {
    if (entry.lastPrompt && !state.lastPrompt) state.lastPrompt = entry.lastPrompt as string;
    return;
  }

  // file-history-snapshot and other non-message entries are excluded.
  if (type !== "user" && type !== "assistant") return;
  if (entry.isMeta) return;

  const msg = entry.message as Record<string, unknown> | undefined;

  // A compaction summary is generated text, not a prompt: keep it out of the
  // counts, previews and snippets. It is still a message in the conversation
  // reducer, so the page total must keep counting it.
  if (entry.isCompactSummary === true) {
    if (extractTextContent(msg?.content)) state.pageMessageCount++;
    return;
  }

  if (state.model === null && msg?.model) state.model = msg.model as string;

  if (type === "user" && !state.firstUserSeen) {
    state.firstUserSeen = true;
    if (isTeammateContent(msg?.content)) state.isTeammate = true;
  }

  const content = extractTextContent(msg?.content);
  const hasToolUseResult = type === "user" && entry.toolUseResult != null;
  const isOnlyToolResult = hasToolUseResult && isOnlyToolResultContent(msg?.content);

  const toolSet = new Set(state.toolNames);
  collectToolNames(msg?.content, toolSet);
  state.toolNames = Array.from(toolSet);

  // Count messages the same way parseConversation does, so bounded paging has a
  // correct total. (Broader than messageCount: includes tool_use-only and
  // thinking-only lines.)
  const toolUseBlocks = extractToolUseBlocks(msg?.content);
  const thinking = type === "assistant" ? extractThinking(msg?.content) : null;
  const hasThinking = !!(thinking?.content || thinking?.signature);
  if (content || isOnlyToolResult || toolUseBlocks.length > 0 || hasThinking) {
    state.pageMessageCount++;
  }

  if (content || isOnlyToolResult) {
    state.messageCount++;
    state.lastMessageSender = type as MessageSender;

    if (content) {
      const ts = (entry.timestamp as string) || "";
      if (!state.firstMessage) state.firstMessage = { text: content.slice(0, 200), timestamp: ts };
      state.lastMessage = { text: content.slice(0, 200), timestamp: ts };

      if (state.previewLength < tier.previewMax) {
        state.previewParts.push(content);
        state.previewLength += content.length;
      }
      if (state.snippetLength < tier.snippetMax) {
        const remaining = tier.snippetMax - state.snippetLength;
        const chunk = content.length > remaining ? content.slice(0, remaining) : content;
        state.snippetParts.push(chunk);
        state.snippetLength += chunk.length;
      }
    }
  }
}

// Build the ConversationMeta from accumulated state, or null when no messages
// were seen — matching parseMeta()'s final shape exactly.
export function finalizeMeta(
  state: ReducerState,
  filePath: string,
  account: string,
  tier: ContentTier,
): ConversationMeta | null {
  if (state.messageCount === 0) return null;

  // The explicit JSONL fields are authoritative and platform-independent. The
  // path shape is only a fallback for a transcript that carries neither.
  const isSidechain = state.isSidechain && !!state.agentId;
  const isSubagentPath = SUBAGENT_PATH_RE.test(filePath);
  const isSubagent = isSidechain || isSubagentPath;

  let parentSessionId: string | null = null;
  if (isSubagentPath) {
    const uuidDir = dirname(dirname(filePath));
    parentSessionId = join(dirname(uuidDir), `${basename(uuidDir)}.jsonl`);
  }

  const projectPath = state.cwd;
  return {
    id: filePath,
    filePath,
    provider: CLAUDE_CODE_PROVIDER,
    sessionId: state.sessionId || basename(filePath, ".jsonl"),
    sessionName: resolveSessionName(state.nameSlots).name ?? "",
    nameSlots: state.nameSlots,
    projectPath,
    projectName: getShortProjectName(projectPath),
    account,
    timestamp: state.latestTimestamp || new Date().toISOString(),
    messageCount: state.messageCount,
    lastMessageSender: state.lastMessageSender,
    preview: state.previewParts.join(" ").slice(0, tier.previewMax),
    contentSnippet: state.snippetParts.join(" "),
    gitBranch: null,
    model: state.model,
    isSubagent,
    parentSessionId,
    subagentId: isSidechain ? state.agentId : undefined,
    // A sidechain line's `sessionId` IS the parent's id — that is exactly the
    // conflation `subagentId` exists to undo, so it is the parent UUID here.
    parentSessionUuid: isSidechain && state.sessionId ? state.sessionId : undefined,
    isTeammate: state.isTeammate,
    teamName: state.teamName || null,
    toolNames: state.toolNames,
    firstMessage: state.firstMessage,
    lastMessage: state.lastMessage,
    lastPrompt: state.lastPrompt || undefined,
  };
}

// Both separators: the scanner is handed NATIVE paths, so a hardcoded "/" makes
// this test silently false on Windows — a clean false negative that disables
// path-based subagent detection there without any error.
const SUBAGENT_PATH_RE = /[\\/]subagents[\\/]/;

function getShortProjectName(fullPath: string): string {
  const parts = fullPath.split("/").filter(Boolean);
  return parts.slice(-3).join("/");
}
